import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { userService, profileService } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';

interface Profile { id: number; name: string; description?: string }

const UserFormPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isAdmin: isCurrentAdmin } = useAuth();
    const isEdit = !!id;

    const [form, setForm] = useState({
        name: '',
        lastname: '',
        email: '',
        username: '',
        password: '',
        password_confirmation: '',
        description: '',
        status: 'A' as 'A' | 'I',
        profile_id: '',
        is_admin: false,
    });
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadRefs = useCallback(async () => {
        const p = await profileService.getAll({ per_page: 100 });
        setProfiles((p.data ?? []) as Profile[]);
    }, []);

    useEffect(() => {
        loadRefs();
        if (isEdit) {
            setLoading(true);
            userService.getById(Number(id))
                .then(u => {
                    setForm({
                        name: u.name ?? '',
                        lastname: (u as any).lastname ?? '',
                        email: u.email ?? '',
                        username: (u as any).username ?? '',
                        password: '',
                        password_confirmation: '',
                        description: (u as any).description ?? '',
                        status: (u as any).status ?? 'A',
                        profile_id: String((u as any).profile_id ?? ''),
                        is_admin: (u as any).is_admin ?? false,
                    });
                })
                .catch(() => setError('Error al cargar usuario'))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit, loadRefs]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isEdit && form.password !== form.password_confirmation) {
            setError('Las contraseñas no coinciden');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload: any = {
                name: form.name,
                lastname: form.lastname,
                email: form.email,
                username: form.username.trim().toLowerCase(),
                description: form.description || undefined,
                status: form.status,
                profile_id: form.profile_id ? Number(form.profile_id) : undefined,
                is_admin: form.is_admin,
            };
            if (!isEdit) {
                payload.password = form.password;
            } else if (form.password) {
                payload.password = form.password;
            }

            if (isEdit) {
                await userService.update(Number(id), payload);
            } else {
                await userService.create(payload);
            }
            navigate('/admin/users');
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.response?.data?.errors;
            if (typeof msg === 'object') {
                setError(Object.values(msg).flat().join(' '));
            } else {
                setError(msg || 'Error al guardar usuario');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Spinner variant="full-page" text="Cargando..." size="lg" />;
    }

    return (
        <>
            <div className="page-header">
                <div className="row">
                    <div className="col-sm-12">
                        <Breadcrumb items={[{ label: 'Usuarios', to: '/admin/users' }, { label: isEdit ? 'Editar Usuario' : 'Nuevo Usuario' }]} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-8">
                    <div className="card">
                        <div className="card-body">
                            <h4 className="card-title mb-3">
                                {isEdit ? 'Editar Usuario' : 'Nuevo Usuario'}
                            </h4>

                            {error && (
                                <div className="alert alert-danger alert-dismissible">
                                    <button className="btn-close" onClick={() => setError(null)}></button>
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Nombre *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={form.name}
                                            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Apellido *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={form.lastname}
                                            onChange={e => setForm(p => ({ ...p, lastname: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" htmlFor="user-email">Email *</label>
                                        <input
                                            id="user-email"
                                            type="email"
                                            className="form-control"
                                            value={form.email}
                                            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" htmlFor="user-username">Usuario *</label>
                                        <input
                                            id="user-username"
                                            type="text"
                                            className="form-control"
                                            value={form.username}
                                            onChange={e => setForm(p => ({ ...p, username: e.target.value.toLowerCase() }))}
                                            required
                                            minLength={3}
                                            maxLength={50}
                                            pattern="[a-z0-9._-]+"
                                            placeholder="lgarcia"
                                            autoComplete="username"
                                        />
                                        <small className="text-muted">3–50 caracteres: letras minúsculas, números, . _ -</small>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Estado</label>
                                        <select
                                            className="form-select"
                                            value={form.status}
                                            onChange={e => setForm(p => ({ ...p, status: e.target.value as 'A' | 'I' }))}
                                        >
                                            <option value="A">Activo</option>
                                            <option value="I">Inactivo</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            {isEdit ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={form.password}
                                            onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                            required={!isEdit}
                                            minLength={8}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Confirmar Contraseña {!isEdit && '*'}</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={form.password_confirmation}
                                            onChange={e => setForm(p => ({ ...p, password_confirmation: e.target.value }))}
                                            required={!isEdit}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Perfil</label>
                                        <select
                                            className="form-select"
                                            value={form.profile_id}
                                            onChange={e => setForm(p => ({ ...p, profile_id: e.target.value }))}
                                        >
                                            <option value="">Sin perfil (acceso total)</option>
                                            {profiles.map(p => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                            ))}
                                        </select>
                                        <small className="text-muted">Sin perfil = acceso a todo el sistema</small>
                                    </div>
                                    {isCurrentAdmin() && (
                                        <div className="col-md-6">
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="isAdminToggle"
                                                    checked={form.is_admin}
                                                    onChange={e => setForm(p => ({ ...p, is_admin: e.target.checked }))}
                                                />
                                                <label className="form-check-label" htmlFor="isAdminToggle">
                                                    Es administrador
                                                </label>
                                            </div>
                                            <small className="text-muted">Los administradores tienen acceso total al sistema</small>
                                        </div>
                                    )}
                                    <div className="col-md-12">
                                        <label className="form-label">Descripción</label>
                                        <textarea
                                            className="form-control"
                                            rows={2}
                                            value={form.description}
                                            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex gap-2 mt-4">
                                    <button type="submit" className="btn btn-primary" disabled={saving}>
                                        {saving
                                            ? <Spinner size="sm" text="Guardando..." />
                                            : <><i className="fa fa-save me-1"></i> {isEdit ? 'Actualizar' : 'Crear Usuario'}</>}
                                    </button>
                                    <Link to="/admin/users" className="btn btn-outline-secondary">
                                        Cancelar
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UserFormPage;
